import React, { useState, useEffect, useCallback } from "react";
import http from "../Utils/axiosConfig";
import ToastService from "../Utils/ToastService";

const INIT_MEMBER_EDIT = {
  username: "",
  en_name: "",
  mr_name: "",
  dairy_id: "",
  phone: "",
  type: "cow",
  password: "",
};

const DairyAdminsList = ({ secretKey }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState({});

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ password: "", multi: "", email: "" });
  const [savingId, setSavingId] = useState(null);

  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [membersById, setMembersById] = useState({});
  const [membersLoading, setMembersLoading] = useState(false);
  const [revealedMembers, setRevealedMembers] = useState({});

  const [editingMemberId, setEditingMemberId] = useState(null);
  const [memberEditForm, setMemberEditForm] = useState(INIT_MEMBER_EDIT);
  const [savingMemberId, setSavingMemberId] = useState(null);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.post("login/list-dairy-admins", { secretKey });
      setAdmins(res.code === "DATA_FOUND" ? res.data : []);
    } catch (err) {
      console.error("List dairy admins error:", err);
      ToastService.error("Failed to load dairy admins");
    } finally {
      setLoading(false);
    }
  }, [secretKey]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const toggleReveal = (id) => {
    setRevealed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const startEdit = (admin) => {
    setEditingId(admin.id);
    setEditForm({ password: "", multi: String(admin.multi ?? ""), email: admin.email || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ password: "", multi: "", email: "" });
  };

  const saveEdit = async (id) => {
    if (editForm.multi === "" || Number(editForm.multi) <= 0) {
      ToastService.error("Bank multiplier must be greater than 0");
      return;
    }
    setSavingId(id);
    try {
      const payload = { secretKey, id, multi: Number(editForm.multi), email: editForm.email.trim() };
      if (editForm.password) payload.password = editForm.password;

      const res = await http.post("login/update-dairy-admin", payload);
      if (res.code === "DAIRY_ADMIN_UPDATED") {
        ToastService.success("Dairy admin updated successfully");
        setEditingId(null);
        setEditForm({ password: "", multi: "", email: "" });
        fetchAdmins();
      } else if (res.code === "INVALID_EMAIL") {
        ToastService.error("Enter a valid email address");
      } else {
        ToastService.error(res.message || "Failed to update dairy admin");
      }
    } catch (err) {
      console.error("Update dairy admin error:", err);
      ToastService.error("Internal Server error.");
    } finally {
      setSavingId(null);
    }
  };

  const fetchMembers = useCallback(
    async (mstId) => {
      setMembersLoading(true);
      try {
        const res = await http.post("login/list-dairy-members", {
          secretKey,
          mst_id: mstId,
        });
        setMembersById((prev) => ({
          ...prev,
          [mstId]: res.code === "DATA_FOUND" ? res.data : [],
        }));
      } catch (err) {
        console.error("List dairy members error:", err);
        ToastService.error("Failed to load users");
      } finally {
        setMembersLoading(false);
      }
    },
    [secretKey],
  );

  const showMembers = async (admin) => {
    if (selectedAdmin?.id === admin.id) {
      setSelectedAdmin(null);
      return;
    }
    setSelectedAdmin(admin);
    setEditingMemberId(null);
    if (!membersById[admin.id]) {
      await fetchMembers(admin.id);
    }
  };

  const toggleRevealMember = (id) => {
    setRevealedMembers((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const startEditMember = (m) => {
    setEditingMemberId(m.id);
    setMemberEditForm({
      username: m.username || "",
      en_name: m.en_name || "",
      mr_name: m.mr_name || "",
      dairy_id: m.dairy_id != null ? String(m.dairy_id) : "",
      phone: m.phone || "",
      type: m.type || "cow",
      password: "",
    });
  };

  const cancelEditMember = () => {
    setEditingMemberId(null);
    setMemberEditForm(INIT_MEMBER_EDIT);
  };

  const saveEditMember = async (id) => {
    setSavingMemberId(id);
    try {
      const payload = { secretKey, id, ...memberEditForm };
      if (!payload.password) delete payload.password;

      const res = await http.post("login/update-dairy-member", payload);
      if (res.code === "DAIRY_MEMBER_UPDATED") {
        ToastService.success("User updated successfully");
        setEditingMemberId(null);
        setMemberEditForm(INIT_MEMBER_EDIT);
        if (selectedAdmin) await fetchMembers(selectedAdmin.id);
      } else if (res.code === "USERNAME_ALREADY_EXISTS") {
        ToastService.error("This username is already taken");
      } else {
        ToastService.error(res.message || "Failed to update user");
      }
    } catch (err) {
      console.error("Update dairy member error:", err);
      ToastService.error("Internal Server error.");
    } finally {
      setSavingMemberId(null);
    }
  };

  if (loading) return <p className="dal-status">Loading...</p>;

  if (admins.length === 0) {
    return <p className="dal-status">No dairy admins found.</p>;
  }

  const members = selectedAdmin ? membersById[selectedAdmin.id] : null;

  return (
    <>
      <div className="dre-table-wrap">
        <table className="dre-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Name</th>
              <th>Email</th>
              <th>Bank Multiplier</th>
              <th>Password</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin) => (
              <tr key={admin.id}>
                <td>{admin.id}</td>
                <td>{admin.username}</td>
                <td>{admin.mr_name || admin.en_name || admin.username}</td>
                <td>
                  {editingId === admin.id ? (
                    <input
                      type="email"
                      className="dre-input dal-inline-input"
                      value={editForm.email}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, email: e.target.value }))
                      }
                    />
                  ) : (
                    admin.email || "-"
                  )}
                </td>
                <td>
                  {editingId === admin.id ? (
                    <input
                      type="number"
                      min="0"
                      className="dre-input dal-inline-input"
                      value={editForm.multi}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, multi: e.target.value }))
                      }
                    />
                  ) : (
                    admin.multi
                  )}
                </td>
                <td>
                  {editingId === admin.id ? (
                    <input
                      type="text"
                      placeholder="Leave blank to keep unchanged"
                      className="dre-input dal-inline-input"
                      value={editForm.password}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, password: e.target.value }))
                      }
                    />
                  ) : (
                    <span className="dal-password">
                      {revealed[admin.id] ? admin.password : "••••••••"}
                      <button
                        type="button"
                        className="dal-icon-btn"
                        title={revealed[admin.id] ? "Hide password" : "Show password"}
                        onClick={() => toggleReveal(admin.id)}
                      >
                        <i className={`fa-solid ${revealed[admin.id] ? "fa-eye-slash" : "fa-eye"}`}></i>
                      </button>
                    </span>
                  )}
                </td>
                <td className="dal-actions">
                  {editingId === admin.id ? (
                    <>
                      <button
                        className="dal-icon-btn dal-icon-btn--save"
                        title="Save"
                        disabled={savingId === admin.id}
                        onClick={() => saveEdit(admin.id)}
                      >
                        <i className={`fa-solid ${savingId === admin.id ? "fa-spinner fa-spin" : "fa-check"}`}></i>
                      </button>
                      <button
                        className="dal-icon-btn dal-icon-btn--cancel"
                        title="Cancel"
                        onClick={cancelEdit}
                      >
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="dal-icon-btn"
                        title="Edit"
                        onClick={() => startEdit(admin)}
                      >
                        <i className="fa-solid fa-pen"></i>
                      </button>
                      <button
                        className={`dal-icon-btn ${selectedAdmin?.id === admin.id ? "dal-icon-btn--active" : ""}`}
                        title="Show Users"
                        onClick={() => showMembers(admin)}
                      >
                        <i className="fa-solid fa-users"></i>
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedAdmin && (
        <div className="dal-members-panel">
          <div className="dal-members-title">
            Users — {selectedAdmin.mr_name || selectedAdmin.en_name || selectedAdmin.username}
          </div>

          {membersLoading ? (
            <p className="dal-status">Loading users...</p>
          ) : members?.length ? (
            <div className="dre-table-wrap">
              <table className="dre-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Dairy ID</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Type</th>
                    <th>Password</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const isEditing = editingMemberId === m.id;
                    return (
                      <tr key={m.id}>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              className="dre-input dal-inline-input"
                              value={memberEditForm.username}
                              onChange={(e) =>
                                setMemberEditForm((p) => ({ ...p, username: e.target.value }))
                              }
                            />
                          ) : (
                            m.username
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              className="dre-input dal-inline-input"
                              value={memberEditForm.dairy_id}
                              onChange={(e) =>
                                setMemberEditForm((p) => ({ ...p, dairy_id: e.target.value }))
                              }
                            />
                          ) : (
                            m.dairy_id
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <div className="dal-stack">
                              <input
                                type="text"
                                placeholder="English Name"
                                className="dre-input dal-inline-input"
                                value={memberEditForm.en_name}
                                onChange={(e) =>
                                  setMemberEditForm((p) => ({ ...p, en_name: e.target.value }))
                                }
                              />
                              <input
                                type="text"
                                placeholder="Marathi Name"
                                className="dre-input dal-inline-input"
                                value={memberEditForm.mr_name}
                                onChange={(e) =>
                                  setMemberEditForm((p) => ({ ...p, mr_name: e.target.value }))
                                }
                              />
                            </div>
                          ) : (
                            m.mr_name || m.en_name
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              className="dre-input dal-inline-input"
                              value={memberEditForm.phone}
                              onChange={(e) =>
                                setMemberEditForm((p) => ({ ...p, phone: e.target.value }))
                              }
                            />
                          ) : (
                            m.phone
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <select
                              className="dre-input dal-inline-input"
                              value={memberEditForm.type}
                              onChange={(e) =>
                                setMemberEditForm((p) => ({ ...p, type: e.target.value }))
                              }
                            >
                              <option value="cow">cow</option>
                              <option value="buffalo">buffalo</option>
                            </select>
                          ) : (
                            m.type
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              placeholder="Leave blank to keep unchanged"
                              className="dre-input dal-inline-input"
                              value={memberEditForm.password}
                              onChange={(e) =>
                                setMemberEditForm((p) => ({ ...p, password: e.target.value }))
                              }
                            />
                          ) : (
                            <span className="dal-password">
                              {revealedMembers[m.id] ? m.password : "••••••••"}
                              <button
                                type="button"
                                className="dal-icon-btn"
                                title={revealedMembers[m.id] ? "Hide password" : "Show password"}
                                onClick={() => toggleRevealMember(m.id)}
                              >
                                <i className={`fa-solid ${revealedMembers[m.id] ? "fa-eye-slash" : "fa-eye"}`}></i>
                              </button>
                            </span>
                          )}
                        </td>
                        <td className="dal-actions">
                          {isEditing ? (
                            <>
                              <button
                                className="dal-icon-btn dal-icon-btn--save"
                                title="Save"
                                disabled={savingMemberId === m.id}
                                onClick={() => saveEditMember(m.id)}
                              >
                                <i className={`fa-solid ${savingMemberId === m.id ? "fa-spinner fa-spin" : "fa-check"}`}></i>
                              </button>
                              <button
                                className="dal-icon-btn dal-icon-btn--cancel"
                                title="Cancel"
                                onClick={cancelEditMember}
                              >
                                <i className="fa-solid fa-xmark"></i>
                              </button>
                            </>
                          ) : (
                            <button
                              className="dal-icon-btn"
                              title="Edit"
                              onClick={() => startEditMember(m)}
                            >
                              <i className="fa-solid fa-pen"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="dal-status">No users found for this dairy.</p>
          )}
        </div>
      )}
    </>
  );
};

export default DairyAdminsList;
